import type { ProfileParams, SendOtpParams, VerifyOtpParams, VerifyOtpResponse } from '@/api/schema'
import { Col, Row } from '@/app/components/Flex/Flex'
import { Input } from '@/app/components/Input/Input'
import { Layout } from '@/app/components/Layout/Layout'
import { Motion } from '@/app/components/Motion/Motion'
import { OAuthBtn } from '@/app/components/OAuthBtn/OAuthBtn'
import { Terms } from '@/app/components/Terms/Terms'
import { Text } from '@/app/components/Text/Text'
import { Lib } from '@/app/components/UI/Lib'
import { UI } from '@/app/components/UI/UI'
import { OnboardingType } from '@/app/const'
import { useBack } from '@/app/hooks/useBack'
import { useCounter } from '@/app/hooks/useCounter'
import { useSession } from '@/app/state/session'
import { api } from '@/app/utils/api'
import { parseError } from '@/app/utils/error'
import { metrikaGoal } from '@/app/utils/metrika'
import type { FunctionalComponent } from 'preact'
import { useEffect, useState } from 'preact/hooks'
import { useSearchParams } from 'wouter-preact'

export const LoginView: FunctionalComponent<{
  type: OnboardingType
  profile?: ProfileParams
  onBack?: () => void
  onSuccess?: () => void
}> = props => {
  const back = useBack('/location')
  const fetchSession = useSession(s => s.fetch)

  const userId = useSession(s => s.user?.id)

  const counter = useCounter(30)

  const [params] = useSearchParams()
  const loginRawError = params.get('error')
  const loginErrors = loginRawError ? parseError(loginRawError) : []

  const fromAnketa = params.get('from') === 'anketa'

  const [email, setEmail] = useState('')
  const [code, setCode] = useState('')
  const [step, setStep] = useState<'email' | 'otp'>('email')
  const [loading, setLoading] = useState(false)
  const [sendLoading, setSendLoading] = useState(false)
  const [errors, setErrors] = useState<string[]>([])

  const submitEmail = async () => {
    try {
      setLoading(true)
      setErrors([])
      counter.reset()

      await api.post(
        '/user/send-otp',
        {
          email,
        } satisfies SendOtpParams,
      )

      metrikaGoal('login_submit_email')
      setStep('otp')
    }
    catch (e) {
      console.error(e)
      setErrors(parseError(e))
    }
    finally {
      setLoading(false)
    }
  }

  const submitCode = async () => {
    try {
      setLoading(true)
      setErrors([])

      await api.post<VerifyOtpResponse>(
        userId ? '/user/email/verify-otp' : '/user/verify-otp',
        {
          email,
          code,
          profile: props.profile,
        } satisfies VerifyOtpParams,
      )

      metrikaGoal('login_submit_code')
      await fetchSession()
      props.onSuccess?.()
    }
    catch (e) {
      console.error(e)
      setErrors(parseError(e))
    }
    finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    metrikaGoal('login')
  }, [])

  return (
    <Layout.Content
      help
      key='login-view'
      onBack={step === 'otp'
        ? () => {
          setErrors([])
          setStep('email')
        }
        : (props.onBack ?? back)}
    >
      <form
        onSubmit={e => {
          e.preventDefault()

          if (step === 'email') {
            submitEmail()
          }
          else {
            submitCode()
          }
        }}
      >
        <Motion>
          {step === 'email'
            ? (
              <Row gap={15}>
                <UI.Avatar
                  label
                  size={64}
                  bot={props.type}
                />

                <Lib.Fields>
                  <Lib.SystemMessage>
                    {fromAnketa
                      ? (
                        <>
                          Спасибо за ответы! Тебе доступно 10 бесплатных сообщений. Укажи email, чтобы получить их.
                        </>
                      )
                      : (
                        props.type === OnboardingType.Login
                          ? (
                            userId
                              ? <>Чтобы не потерять свои разборы и историю чатов, укажи email. Мы привяжем его к твоему аккаунту.</>
                              : <>Войди, чтобы восстановить свои разборы и историю чатов.</>
                          )
                          : <>Войди, чтобы сохранить разборы, историю чатов и вернуться к ним в любое время.</>
                      )}
                  </Lib.SystemMessage>

                  <Input.Block label='Email'>
                    <Input
                      type='email'
                      placeholder='Введи email'
                      disabled={loading || sendLoading}
                      loading={loading}
                      onInput={setEmail}
                      onChange={setEmail}
                      value={email}
                    />
                  </Input.Block>

                  <Lib.Errors errors={[...errors, ...loginErrors]} />
                </Lib.Fields>

                <UI.Button
                  size='l'
                  type='primary'
                  htmlType='submit'
                  disabled={loading}
                >
                  Продолжить
                </UI.Button>

                {!userId && !fromAnketa && loginErrors.length === 0 && (
                  <Col grid gap={10}>
                    <OAuthBtn
                      provider='vk'
                      profile={props.profile}
                    />
                    <OAuthBtn
                      provider='yandex'
                      profile={props.profile}
                    />
                  </Col>
                )}

                <UI.Hint>
                  <Terms btn='Продолжить' />
                </UI.Hint>
              </Row>
            )
            : (
              <Motion>
                <Row gap={15}>
                  <UI.Avatar
                    label
                    size={64}
                    bot={props.type}
                  />

                  <Lib.Fields>
                    <Lib.SystemMessage>
                      Я отправила код на <b>{email || 'твой email'}</b>. Проверь письмо и введи его ниже.
                    </Lib.SystemMessage>

                    <Input.Block label='Email'>
                      <Input
                        disabled
                        value={email}
                        rightBtn={
                          <button
                            type='button'
                            onClick={() => {
                              setErrors([])
                              setStep('email')
                            }}
                          >
                            <Text
                              as='span'
                              fontSize={13}
                            >
                              Изменить
                            </Text>
                          </button>
                        }
                      />
                    </Input.Block>

                    <Input.Block
                      label='Код из письма'
                      action={
                        <button
                          type='button'
                          class='simpleBtn'
                          disabled={counter.counter > 0 || loading || sendLoading}
                          onClick={async () => {
                            try {
                              setSendLoading(true)
                              await submitEmail()
                            }
                            finally {
                              setSendLoading(false)
                            }
                          }}
                        >
                          <Text
                            as='span'
                            fontSize={13}
                          >
                            Отправить снова
                            {(counter.counter > 0 && !sendLoading) && (
                              <>
                                {' '}
                                ({counter.counter})
                              </>
                            )}
                          </Text>
                        </button>
                      }
                    >
                      <Input
                        type='tel'
                        placeholder={'Введи код'}
                        disabled={loading || sendLoading}
                        loading={loading}
                        onInput={setCode}
                        onChange={setCode}
                        value={code}
                        maxLength={4}
                        minLength={4}
                      />
                    </Input.Block>

                    <Lib.Errors errors={errors} />
                  </Lib.Fields>

                  <UI.Button
                    size='l'
                    type='primary'
                    htmlType='submit'
                    disabled={loading}
                  >
                    Подтвердить
                  </UI.Button>
                </Row>
              </Motion>
            )}
        </Motion>
      </form>
    </Layout.Content>
  )
}
